import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import commentService, { Comment } from '../../services/commentService';

interface CommentState {
  comments: Record<string, Comment[]>; // taskId -> comments
  loading: boolean;
  error: string | null;
}

const initialState: CommentState = {
  comments: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchTaskComments = createAsyncThunk(
  'comments/fetchTaskComments',
  async (taskId: string) => {
    const comments = await commentService.getTaskComments(taskId);
    return { taskId, comments };
  }
);

export const createComment = createAsyncThunk(
  'comments/create',
  async (data: any) => {
    return await commentService.createComment(data);
  }
);

export const updateComment = createAsyncThunk(
  'comments/update',
  async ({ id, data }: { id: string; data: any }) => {
    return await commentService.updateComment(id, data);
  }
);

export const deleteComment = createAsyncThunk(
  'comments/delete',
  async ({ id, taskId }: { id: string; taskId: string }) => {
    await commentService.deleteComment(id);
    return { id, taskId };
  }
);

const commentSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addCommentOptimistic: (state, action: PayloadAction<Comment>) => {
      const { taskId } = action.payload;
      if (!state.comments[taskId]) {
        state.comments[taskId] = [];
      }
      state.comments[taskId].push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch comments
      .addCase(fetchTaskComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments[action.payload.taskId] = action.payload.comments;
      })
      .addCase(fetchTaskComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch comments';
      })
      // Create comment
      .addCase(createComment.fulfilled, (state, action: PayloadAction<Comment>) => {
        const { taskId } = action.payload;
        if (!state.comments[taskId]) {
          state.comments[taskId] = [];
        }
        state.comments[taskId].push(action.payload);
      })
      // Update comment
      .addCase(updateComment.fulfilled, (state, action: PayloadAction<Comment>) => {
        const { taskId, id } = action.payload;
        if (state.comments[taskId]) {
          const index = state.comments[taskId].findIndex(c => c.id === id);
          if (index !== -1) {
            state.comments[taskId][index] = action.payload;
          }
        }
      })
      // Delete comment
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { id, taskId } = action.payload;
        if (state.comments[taskId]) {
          state.comments[taskId] = state.comments[taskId].filter(c => c.id !== id);
        }
      });
  },
});

export const { clearError, addCommentOptimistic } = commentSlice.actions;
export default commentSlice.reducer;
